import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  colors: any;
  buildUrlWithFilters: (base: string) => string;
  buildPostBody?: () => any;
  limit?: number; // default 10
};

type Row = {
  tag: string;      // RFID
  size: string;     // e.g. "۷ اینچ"
  installs: number; // Count
};

type SortKey = 'tag' | 'size' | 'installs';

// ───────── App font (force BTitrBold) ─────────
const APP_FONT =
  `"BTitr","B Titr","BTitrBold","IRANSans","Inter",system-ui,"Segoe UI",Roboto,sans-serif`;

// Persian digits helper
const toFaDigits = (v: unknown): string =>
  String(v ?? '')
    .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    .replace(/\./g, '٫')
    .replace(/,/g, '٬');

const SpadesMostUsedCard: React.FC<Props> = ({
  colors,
  buildUrlWithFilters,
  buildPostBody,
  limit = 10,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>('installs');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  // ---- compact tokens
  const S = {
    radiusLg: 14,
    radiusSm: 10,
    padCard: 12,
    padRow: 8,
    gap: 6,
    fsXs: 11,
    fsSm: 12,
    fsMd: 13,
    fsNum: 13,
  };

  // ---- styles
  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: S.radiusLg,
    padding: S.padCard,
    boxShadow: '0 3px 10px rgba(0,0,0,.16)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const title: React.CSSProperties = {
    fontWeight: 900,
    marginBottom: 2,
    letterSpacing: 0.2,
    fontSize: S.fsMd,
    fontFamily: APP_FONT,
  };

  const sub: React.CSSProperties = {
    fontSize: S.fsXs,
    color: colors.sub,
    marginBottom: 6,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const headRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 1.2fr .9fr 78px',
    gap: S.gap,
    padding: `${S.padRow}px ${S.padRow + 2}px`,
    position: 'sticky',
    top: 0,
    background: colors.panel,
    borderBottom: `1px solid ${colors.border}`,
    zIndex: 1,
    fontSize: S.fsXs,
    color: colors.sub,
    fontWeight: 800,
    fontFamily: APP_FONT, // ⬅️ headers use BtitrBold
  };

  const dataRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 1.2fr .9fr 78px',
    gap: S.gap,
    padding: `${S.padRow}px ${S.padRow + 2}px`,
    alignItems: 'center',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: S.fsSm,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const tableWrap: React.CSSProperties = {
    overflowY: 'auto',
    borderRadius: S.radiusSm,
    background: '#0f172a',
    border: `1px solid ${colors.border}`,
  };

  const thBtn = (key: SortKey, label: string) => (
    <button
      onClick={() => {
        if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else {
          setSortBy(key);
          setSortDir(key === 'tag' || key === 'size' ? 'asc' : 'desc');
        }
      }}
      style={{
        textAlign: 'right',
        background: 'transparent',
        border: 'none',
        color: colors.sub,
        cursor: 'pointer',
        fontWeight: 800,
        fontSize: S.fsXs,
        fontFamily: APP_FONT, // ⬅️ ensure header buttons are BtitrBold
      }}
      title="مرتب‌سازی"
    >
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </button>
  );

  const num = (v: number) => v.toLocaleString('fa-IR');
  const fmtSize = (s: any) => {
    const n = Number(s);
    if (!Number.isFinite(n)) return String(s ?? '-');
    return `${n.toLocaleString('fa-IR')} اینچ`;
  };

  // ---- data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr(null);

      const token = localStorage.getItem('token') || '';
      const headersBase: HeadersInit = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      try {
        let res: Response;

        if (buildPostBody) {
          const body = buildPostBody();
          res = await fetch('/api/dashboard/top-used', {
            method: 'POST',
            headers: { ...headersBase, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          });
        } else {
          const url = buildUrlWithFilters('/api/spades/most-used') + `&limit=${limit}`;
          res = await fetch(url, { headers: headersBase, credentials: 'include' });
        }

        const text = await res.text();
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
        const j = text ? JSON.parse(text) : {};

        let mapped: Row[] = [];
        if (Array.isArray(j?.top_used_spade)) {
          mapped = (j.top_used_spade as any[]).map((it: any) => ({
            tag: String(it?.Rfid ?? '-'),
            size: fmtSize(it?.Size),
            installs: Number(it?.Count ?? 0),
          }));
        } else {
          const list = Array.isArray(j) ? j : j?.items ?? j?.data ?? [];
          mapped = list.map((it: any) => ({
            tag: String(it.tag ?? it.tag_epc ?? '-'),
            size: fmtSize(it.size ?? it.spade_size ?? '-'),
            installs: Number(it.installs ?? it.install_count ?? 0),
          }));
        }

        setRows(mapped.slice(0, limit));
      } catch (e: any) {
        setRows([
          { tag: 'E28011606000021729809565', size: fmtSize(7), installs: 1 },
          { tag: 'E280116060000217298073B5', size: fmtSize(7), installs: 1 },
          { tag: 'E28011606000021729809505', size: fmtSize(5), installs: 1 },
        ]);
        setErr(e?.message || 'داده‌های سرور در دسترس نیست (placeholder)');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [buildUrlWithFilters, buildPostBody, limit]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const A = a[sortBy] as any;
      const B = b[sortBy] as any;
      if (typeof A === 'number' && typeof B === 'number') {
        return sortDir === 'desc' ? B - A : A - B;
      }
      return sortDir === 'desc'
        ? String(B).localeCompare(String(A), 'fa')
        : String(A).localeCompare(String(B), 'fa');
    });
    return arr;
  }, [rows, sortBy, sortDir]);

  const maxInstalls = Math.max(1, ...sorted.map((r) => r.installs));

  // ---- render
  return (
    <section style={card} dir="rtl">
      <div style={title}>اسپیدهای با بیشترین استفاده</div>
      <div style={sub}>مرتب‌سازی پیش‌فرض: بیشترین دفعات نصب</div>
      {(loading || err) && (
        <div style={{ ...sub, color: loading ? colors.accent : colors.danger }}>
          {loading ? 'در حال بارگذاری…' : err}
        </div>
      )}

      <div style={tableWrap}>
        <div style={headRow}>
          <div># رتبه</div>
          <div>{thBtn('tag', 'تگ اسپید')}</div>
          <div>{thBtn('size', 'سایز')}</div>
          <div style={{ textAlign: 'right' }}>{thBtn('installs', 'دفعات نصب')}</div>
        </div>

        <div>
          {sorted.map((r, i) => (
            <div
              key={r.tag + i}
              style={{
                ...dataRow,
                background: i % 2 ? 'rgba(255,255,255,0.015)' : 'transparent',
              }}
            >
              {/* رتبه با اعداد فارسی */}
              <div style={{ fontWeight: 800, fontSize: S.fsNum, fontFamily: APP_FONT }}>
                {toFaDigits(i + 1)}
              </div>

              {/* EPC آخرین ۶ کاراکتر (لاتین برای قابلیت جستجو/کپی) — عمدی monospace */}
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 800 }}>
                {r.tag?.slice(-6) || '-'}
              </div>

              {/* سایز با اعداد فارسی */}
              <div style={{ color: colors.sub, fontFamily: APP_FONT, fontWeight: 700 }}>{r.size}</div>

              {/* دفعات نصب با اعداد فارسی + بار */}
              <div>
                <div style={{ textAlign: 'right', fontWeight: 900, fontSize: S.fsNum, fontFamily: APP_FONT }}>
                  {num(r.installs)}
                </div>
                <div
                  style={{
                    height: 4,
                    borderRadius: 6,
                    background: '#0b1220',
                    border: `1px solid ${colors.border}`,
                    overflow: 'hidden',
                    marginTop: 4,
                  }}
                  aria-hidden
                >
                  <div
                    style={{
                      width: `${Math.round((r.installs / maxInstalls) * 100)}%`,
                      height: '100%',
                      background: colors.accent,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

          {sorted.length === 0 && (
            <div style={{ padding: 12, color: colors.sub, fontSize: S.fsSm, fontFamily: APP_FONT }}>
              داده‌ای یافت نشد.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SpadesMostUsedCard;
